<template>
  <div v-if="props.objectCollection && props.objectCollection.info">
    <v-data-table-server
      v-model:items-per-page="props.objectCollection.pagination.pageSize"
      :headers="vHeaders2"
      :items-length="props.objectCollection?.info.size ?? 0"
      :items="props.objectCollection.objects"
      :items-per-page-options="[
        { value: 10, title: '10' },
        { value: 25, title: '25' },
        { value: 50, title: '50' },
        { value: 100, title: '100' },
      ]"
      items-per-page-text="Elemente pro Seite"
      :show-expand="props.hasExpandableRow"
      :loading="props.objectCollection?.status.fetching"
      :header-props="{ class: 'font-weight-bold' }"
      @update:items-per-page="paginationChange"
      @update:sort-by="(sortItem) => emit('sort', sortItem)"
      @update:page="(page) => emit('page-change', page)"
    >
      <template #top>
        <slot name="top" />
      </template>
      <template #item="{ item, index, toggleExpand, isExpanded, internalItem }">
        <v-hover>
          <template #default="{ isHovering, props: hoveringProps }">
            <tr
              v-bind="hoveringProps"
              :style="{
                'background-color': isHovering ? '#e5e5e5' : 'inherit',
              }"
              :class="{
                'cursor-pointer': props.canSelect || props.hasDetailView,
              }"
              @click="onSelect(item)"
            >
              <td v-for="header in vHeaders2" :key="header.key">
                <div v-if="header.key === 'data-table-expand'" @click.stop>
                  <v-btn
                    variant="text"
                    color="primary"
                    :icon="
                      isExpanded(internalItem)
                        ? 'mdi-chevron-up'
                        : 'mdi-chevron-down'
                    "
                    @click="toggleExpand(internalItem)"
                  />
                </div>
                <div
                  v-if="props.hasActions && header.key === 'actions'"
                  as="section"
                  class="d-flex ga-2"
                  @click.stop
                >
                  <v-btn
                    v-if="props.hasDetailView"
                    v-tooltip:bottom="i18n.global.t('components.common.detail')"
                    icon="mdi-information-outline"
                    size="small"
                    variant="outlined"
                    :color="actionButtonColorMap['is-info']"
                    @click.stop="onDetailHandler(item)"
                  />
                  <v-btn
                    v-if="props.canCopy"
                    v-tooltip:bottom="i18n.global.t('components.common.copy')"
                    size="small"
                    variant="outlined"
                    icon="mdi-content-copy"
                    :color="actionButtonColorMap['is-info']"
                    @click.stop="copyDetailItemUrl(item)"
                  />
                  <template
                    v-if="props.modelClass.insertBefore && props.canReorder"
                  >
                    <v-btn
                      v-tooltip:bottom="
                        i18n.global.t('components.common.moveUp')
                      "
                      size="small"
                      variant="outlined"
                      icon="mdi-arrow-up"
                      :color="actionButtonColorMap['is-info']"
                      @click.stop="moveUp(index)"
                    />
                    <v-btn
                      v-tooltip:bottom="
                        i18n.global.t('components.common.moveDown')
                      "
                      size="small"
                      variant="outlined"
                      icon="mdi-arrow-down"
                      :color="actionButtonColorMap['is-info']"
                      @click.stop="moveDown(index)"
                    />
                  </template>
                  <v-btn
                    v-if="props.canCopyData"
                    v-tooltip:bottom="
                      'Single click: Copy ID.\nDouble click: Copy Row Data.'
                    "
                    :color="actionButtonColorMap['is-info']"
                    icon="mdi-clipboard-outline"
                    size="small"
                    variant="outlined"
                    @click.stop="copyRowData(item)"
                  />
                  <v-btn
                    v-if="props.canDelete && props.modelClass.canDelete()"
                    v-tooltip:bottom="i18n.global.t('components.common.delete')"
                    :color="actionButtonColorMap['is-danger']"
                    icon="mdi-close"
                    variant="outlined"
                    size="small"
                    @click.stop="confirmDelete(item)"
                  />
                  <v-btn
                    v-for="customAction in props.customActions"
                    :key="customAction.label"
                    v-tooltip:bottom="customAction.label"
                    variant="outlined"
                    :class="{
                      'cursor-not-allowed': isActionDisabled(
                        customAction,
                        item
                      ),
                    }"
                    :icon="customAction.icon"
                    size="small"
                    :color="actionButtonColorMap[customAction.type]"
                    :disabled="isActionDisabled(customAction, item)"
                    @click.stop="() => callCustomActionCb(customAction, item)"
                  />
                </div>

                <div
                  v-if="
                    props.canSelect &&
                    header.key === 'select' &&
                    isSelectable(item.id)
                  "
                  as="section"
                  class="d-flex ga-2"
                  @click.stop
                >
                  <v-btn
                    :color="
                      rowSelections[item.id] !== undefined
                        ? actionButtonColorMap['is-success']
                        : actionButtonColorMap['is-info']
                    "
                    :icon="
                      rowSelections[item.id] !== undefined
                        ? 'mdi-check'
                        : 'mdi-crop-free'
                    "
                    variant="outlined"
                    size="small"
                    @click.stop="onSelect(item)"
                  />
                </div>
                <template v-else>
                  <BaseListCell
                    v-for="(cell, cellIndex) of header.cells"
                    :key="cellIndex"
                    :row="parseModel(item)"
                    :column="header"
                    :cell="cell"
                    :value="getValue(cellIndex, item, header)"
                    :index="cellIndex"
                    @cell-edited="handleCellEdition"
                /></template>
              </td>
            </tr>
          </template>
        </v-hover>
      </template>
      <template #expanded-row="{ item, columns: tableCols }">
        <tr>
          <td :colspan="tableCols.length">
            <slot name="detail" :row="item" />
          </td>
        </tr>
      </template>
      <template #no-data>
        <v-empty-state
          icon="mdi-cancel"
          :title="$t('components.common.empty')"
        />
      </template>
    </v-data-table-server>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useSnackbar } from 'vuetify-use-dialog'
import { useStore } from 'vuex'

import { apiClientV2, ApiListSubscription, Context } from '@/api/ApiClientV2'
import { clientAppRouteName } from '@/apps/clientAppRegistry'
import { getClientAppOfRoute } from '@/apps/routingUtils'
import { i18n } from '@/lang/setup'
import {
  BaseListEventType,
  LifeCycleState,
  TransientBaseObjectType,
} from '@/models/core/base'
import routerHandler from '@/router/routerHandler'
import { useCopy } from '@/util/clipboard'
import { useConfirmationDialog } from '@/util/composables/useConfirmationDialog'

import BaseListCell from './BaseListCell.vue'
import { useRowsSelection } from './composables/useRowsSelection'
import type { Column, CustomAction, Header } from './interfaces'

type BaseListTableProps = {
  modelClass: TransientBaseObjectType
  objectCollection: ApiListSubscription<any> | null
  defaultSortOrder?: string
  columns: Column[]
  singleSelection?: boolean
  preSelectedRows?: string[]
  unselectableRows?: string[]
  detailRouteName?: string
  customDelete?: Function | null
  hasActions?: boolean
  canCopy?: boolean
  canAdd?: boolean
  canDelete?: boolean
  canSelect?: boolean
  canReorder?: boolean
  canCopyData?: boolean
  customActions: CustomAction[]
  hasDetailView?: boolean
  customDetail?: Function | null
  detailExtraParams?: Function
  isStriped?: boolean
  hasExpandableRow?: boolean
  errorMessage?: {
    cantSelectRow: string
  }
}

const props = withDefaults(defineProps<BaseListTableProps>(), {
  defaultSortOrder: 'desc',
  singleSelection: false,
  preSelectedRows: () => [],
  unselectableRows: () => [],
  detailRouteName: '',
  customDelete: null,
  hasActions: true,
  canCopy: false,
  canAdd: true,
  canDelete: true,
  canSelect: false,
  canReorder: true,
  canCopyData: true,
  customActions: () => [],
  hasDetailView: true,
  customDetail: null,
  detailExtraParams: () => {},
  isStriped: false,
  hasExpandableRow: false,
  errorMessage: () => ({
    cantSelectRow: 'Row cannot be selected',
  }),
})

const { t } = useI18n()
const createSnackbar = useSnackbar()
const store = useStore()
const route = useRoute()
const router = useRouter()
const { copyTextToClipboard } = useCopy()
const { selectedToEmit, rowSelections, select, isSelectable } =
  useRowsSelection({
    unselectableRows: props.unselectableRows,
    singleSelection: props.singleSelection,
    preSelectedRows: props.preSelectedRows,
    objectCollections: props.objectCollection,
  })

const emit = defineEmits([
  'row-selection',
  'error',
  'page-change',
  'sort',
  'base-list-event',
])

const delay = ref(400)
const clicks = ref(0)
const timer = null

const vHeaders2 = computed(() => {
  const newheaders: Header[] = []

  // Insert columns before all data columns

  if (props.hasExpandableRow)
    newheaders.push({
      title: '',
      key: 'data-table-expand',
      sortable: false,
    })

  const mappedHeadersFromProps: Header[] = props.columns.map((column) => ({
    ...column,
    title: i18n.global.t(column.label),
    key: column.key,
    sortable: column.sortable ?? false,
    align: column.align ?? 'start',
  }))
  newheaders.push(...mappedHeadersFromProps)

  // Insert columns after all data columns

  if (props.hasActions)
    newheaders.push({
      title: t('components.common.actions'),
      key: 'actions',
      sortable: false,
    })

  if (props.canSelect)
    newheaders.push({
      title: t('components.common.select'),
      key: 'select',
      sortable: false,
    })

  return newheaders
})

const context = (row) => {
  return {
    [props.modelClass.objectType]: row.id,
    organisation: store.getters['global/organisation'].id,
  }
}

const getValue = (index: number, row, column: Column) => {
  row = parseModel(row)
  if (Array.isArray(row[column.key])) {
    return row[column.key][index]
  } else {
    return row[column.key]
  }
}

const parseModel = (model: object) => {
  return props.modelClass.parseModel(model, props.modelClass.listFields)
}

const attemptToSelect = (row: any): void => {
  if (!isSelectable(row.id)) {
    createSnackbar({
      text: props.errorMessage.cantSelectRow,
      snackbarProps: { color: 'yellow-darken-3' },
    })
    return
  }
  select(row)
  emit('row-selection', selectedToEmit.value)
}

const onSelect = (row) => {
  if (props.canSelect) {
    attemptToSelect(row)
    return
  }
  if (props.hasDetailView) {
    router.push(getDetailLinkTo(row))
  }
}

const paginationChange = (pageSize: number) => {
  // TODO: use routerHandler
  const redirect = {
    path: route.path,
    query: {
      ...route.query,
      page_size: pageSize,
      page: undefined,
    },
  }
  router.push(redirect)
}

// Cell Edition Handler

const handleCellEdition = async (row, column: Column, newValue) => {
  try {
    if (!row[column.key]) {
      throw new Error(`Invalid field: ${column.key}`)
    }
    row[column.key] = newValue
    await apiClientV2.update(props.modelClass, row)
    createSnackbar({
      text: i18n.global.t('components.common.saveSuccess'),
      snackbarProps: { color: 'green-darken-3' },
    })
    emit('base-list-event', BaseListEventType.UPDATE)
  } catch (error) {
    handleError(error)
  }
}

const refreshCollection = async () => {
  await props.objectCollection?.refresh()
}

const handleError = (error) => {
  emit('error', error)
}

// ACTIONS

/**
 * Manage color of action button depending on type
 */
const actionButtonColorMap = {
  'is-info': 'primary',
  'is-danger': 'error',
  'is-success': 'success',
  'is-warning': 'warning',
}

// Redirect to detail view action

const onDetailHandler = (row) => {
  if (props.hasDetailView) {
    const detailLinkTo = getDetailLinkTo(row)
    router.push(detailLinkTo)
  }
}
const getDetailRouteName = () => {
  if (props.detailRouteName === '') {
    const clientApp = getClientAppOfRoute(route)
    return clientApp
      ? clientAppRouteName(
          clientApp.view_id,
          props.modelClass.objectType + '-detail'
        )
      : ''
  }
  return props.detailRouteName
}

const getDetailLinkTo = (row, copy: boolean = false) => {
  if (props.customDetail !== null) {
    return props.customDetail(row)
  }

  const extraQuery = props.modelClass.detailLinkQuery(context(row))

  const detailLink = routerHandler.getDetailLinkTo(
    getDetailRouteName(),
    row.id,
    copy,
    props.detailExtraParams,
    extraQuery
  )
  return detailLink
}

// Copy link to row detail action

const copyDetailItemUrl = (row) => {
  // todo retrieve the
  const detailLink = getDetailLinkTo(row)
  copyTextToClipboard(detailLink)
}

// Copy row data action

const copyRowData = (row) => {
  clicks.value++
  if (clicks.value === 1) {
    // We copy the ID already because Firefox does not allow this function inside the timeout handler
    copyTextToClipboard(row.id)
    timer.value = setTimeout(() => {
      createSnackbar({
        text: 'Copied ID!',
        snackbarProps: { color: 'green-darken-3' },
      })
      clicks.value = 0
    }, delay.value)
  } else {
    clearTimeout(timer)
    copyTextToClipboard(JSON.stringify(row, null, 2))
    createSnackbar({
      text: 'Copied data!',
      snackbarProps: { color: 'green-darken-3' },
    })
    clicks.value = 0
  }
}

// Item ordering action

const insertBefore = async (
  insert: TransientBaseObjectType,
  before: TransientBaseObjectType
) => {
  // If 'before' is null, the item is moved to the end of the list
  if (insert == null) {
    return
  }
  try {
    await props.modelClass.insertBefore(insert, before)
    emit('base-list-event', BaseListEventType.REORDER)
    refreshCollection()
  } catch (error) {
    handleError(error)
  }
}

const moveUp = async (index: number) => {
  // Function ignore pagination
  const objects = props.objectCollection?.objects
  if (index === 0) {
    // Do nothing, already at the top
  } else {
    // any other element on this page
    insertBefore(objects[index], objects[index - 1])
  }
}

const moveDown = async (index) => {
  // Function ignore pagination
  const objects = props.objectCollection?.objects
  if (index === objects.length - 1) {
    // Do nothing, already at the bottom
    return
  }
  let before = null
  if (objects[index + 2]) {
    before = objects[index + 2]
  }
  insertBefore(objects[index], before)
}

// Delete Item Action

const confirm = useConfirmationDialog()

const confirmDelete = async (object: TransientBaseObjectType) => {
  const result = await confirm({
    content: i18n.global.t('components.common.confirmDelete'),
  })
  if (result) {
    deleteItem(object)
  }
}
const deleteItem = (object: TransientBaseObjectType) => {
  if (props.customDelete !== null) {
    props.customDelete(object)
    return
  } else {
    deleteObject(object)
  }
}

const deleteObject = async (object: TransientBaseObjectType) => {
  let purge = false
  if (object.object_state === LifeCycleState.Deleted) {
    purge = true
  }
  try {
    await apiClientV2.delete(props.modelClass, object.id, purge)
    createSnackbar({
      text: i18n.global.t('components.common.deleteSuccess'),
      snackbarProps: { color: 'green-darken-3' },
    })
    emit('base-list-event', BaseListEventType.DELETE)
  } catch (error) {
    handleError(error)
  }
}

// Custom actions

const callCustomActionCb = async (customAction, rowItem) => {
  try {
    await customAction.callback(rowItem)
  } catch (err) {
    createSnackbar({
      text: err.message,
      snackbarProps: { color: 'red' },
    })
    handleError(err)
  }
}
const isActionDisabled = (customAction, rowItem) => {
  return typeof customAction.disabled === 'function'
    ? customAction.disabled(rowItem)
    : customAction.disabled
}
</script>

<style lang="scss"></style>
